import { useEffect, type Ref } from 'react';
import { ChevronUp } from 'lucide-react';
import { useZoeConversation } from '../../contexts/ZoeConversationContext';
import { Button } from '@/components/ui/button';
import { isLikelyProfitQuestion } from '../../utils/zoeInvestigationErrors';
import { ErrorState } from '@/components/ui/error-state';
import ZoeArchivedAnswer from './ZoeArchivedAnswer';
import ZoeClarificationCard from './ZoeClarificationCard';
import ZoeThinkingState from './ZoeThinkingState';
import ZoeTurnCard from './ZoeTurnCard';
import ZoeUserMessage from './ZoeUserMessage';

/** Humanises an agent id ('data-quality-agent' -> 'Data Quality Agent') as a
 *  fallback when the `GET /agents` registry lookup misses. */
function humanizeAgentId(id: string): string {
  return id
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

interface ZoeThreadProps {
  /** Attached to the newest turn, for scrolling it into view. */
  latestTurnRef?: Ref<HTMLDivElement>;
  /** Spacing between turns; the pop-up sets its own. */
  className?: string;
}

/**
 * I1-E6 — the conversation thread: user → ZOE → user → ZOE, oldest first.
 * Every turn is the user's message followed by ZOE's response to it: an
 * answer, a question back (T29), or, for a turn recorded before full
 * responses were kept, the answer's text (T30). Only the last turn can still
 * be loading; earlier turns are settled history. Shared by the full ZOE page
 * and the pop-up, both reading ZoeConversationContext, so a question back or
 * an older message reads the same in either.
 */
export default function ZoeThread({ latestTurnRef, className = 'space-y-5' }: ZoeThreadProps) {
  const {
    turns,
    isLoading,
    agentLabels,
    activeProfitDrilldown,
    setActiveProfitDrilldown,
    ask,
    retry,
    openProfitabilityReport,
    markTurnRevealed,
    ensureAgentLabels,
    hasEarlier,
    loadingEarlier,
    loadEarlierMessages,
  } = useZoeConversation();

  useEffect(() => {
    ensureAgentLabels();
  }, [ensureAgentLabels]);

  return (
    <>
      {hasEarlier ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadEarlierMessages()}
            loading={loadingEarlier}
            className="h-auto rounded-full py-1 text-muted-foreground hover:border-accent hover:text-foreground hover:bg-transparent"
          >
            {!loadingEarlier ? <ChevronUp className="h-3 w-3" aria-hidden /> : null}
            Show earlier messages
          </Button>
        </div>
      ) : null}

      <div role="log" aria-live="polite" aria-label="Conversation with ZOE" className={className}>
        {turns.map((turn, idx) => {
          const isLatest = idx === turns.length - 1;
          const nextTurn = turns[idx + 1];
          const agentLabel = turn.result?.agent_id
            ? agentLabels[turn.result.agent_id] ?? humanizeAgentId(turn.result.agent_id)
            : undefined;
          const clarification =
            turn.status === 'success' && turn.result?.status === 'needs_clarification'
              ? turn.result.clarification
              : undefined;
          // Marks the option this card was answered with, once it has been.
          const chosenOptionId =
            clarification
            && nextTurn?.answeredClarification?.clarification_id === clarification.clarification_id
              ? nextTurn.answeredClarification.option_id
              : undefined;

          return (
            <div
              key={turn.id}
              ref={isLatest ? latestTurnRef : undefined}
              data-turn-id={turn.id}
              className="space-y-3 scroll-mt-4"
            >
              <ZoeUserMessage
                text={turn.question}
                isClarificationReply={Boolean(turn.answeredClarification)}
              />

              {turn.status === 'loading' && (
                <ZoeThinkingState model={turn.thinkingModel} startedAt={turn.thinkingStartedAt} />
              )}

              {turn.status === 'error' && (
                <ErrorState
                  message={turn.error ?? ''}
                  hint={
                    isLikelyProfitQuestion(turn.question)
                      ? 'Try again shortly, or ask with a shorter date range.'
                      : 'Try again shortly, or ask a more specific question.'
                  }
                  onRetry={isLatest ? retry : undefined}
                  isLoading={false}
                />
              )}

              {/* T29 — ZOE stopped to ask which question, period or count was
                  meant. In place of the answer card: a clarification turn
                  carries no claims, so there is nothing for that card to render. */}
              {clarification && (
                <ZoeClarificationCard
                  clarification={clarification}
                  interactive={isLatest}
                  isLoading={isLoading}
                  chosenOptionId={chosenOptionId}
                  onSelect={(clarificationId, option) => {
                    // The label becomes the user's reply, so the thread reads
                    // as what they meant rather than as "2". The ids bind the
                    // turn to the clarification: the server re-reads the chosen
                    // wording from its own record, so a stale or tampered label
                    // cannot reach the planner.
                    void ask(option.label, {
                      clarificationResponse: { clarification_id: clarificationId, option_id: option.id },
                    });
                  }}
                  // Unbound, like a reply typed in the question box: there is
                  // no option for the server to check it against.
                  onFreeText={(text) => void ask(text)}
                />
              )}

              {turn.status === 'success' && !turn.result && turn.archivedAnswer ? (
                <ZoeArchivedAnswer text={turn.archivedAnswer} />
              ) : null}

              {turn.status === 'success' && turn.result && !clarification && (
                <ZoeTurnCard
                  result={turn.result}
                  agentLabel={agentLabel}
                  activeProfitDrilldownId={
                    activeProfitDrilldown
                      ? `${activeProfitDrilldown.type}|${activeProfitDrilldown.id}`
                      : null
                  }
                  onProfitDrilldown={(d) => {
                    setActiveProfitDrilldown(d);
                    openProfitabilityReport(d);
                  }}
                  onOpenProfitabilityReport={openProfitabilityReport}
                  onSelectFollowUp={(q) => void ask(q)}
                  revealed={turn.revealed}
                  onRevealed={() => markTurnRevealed(turn.id)}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
